import { Location } from 'history';
import React, { Children, ComponentType, FC, forwardRef, PropsWithChildren } from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { TeamRole } from '../../../../../../typings/TeamMember.interface';
import { withStaticProperties } from '../../../utils/withStaticProperties';
import { useAuthorized, usePermissionAwareDisabled } from '../../contexts/TeamPermissionContext';
import { StyledDropdown } from '../Dropdown';
import DropdownMenu, { DropdownMenuOption } from '../DropdownMenu';
import { Div, getStyledUtils, StyledUtilsProps } from '../helpers/StyledUtils';
import Badge from './Badge';
import Icon, { IconName, StyledIcon } from './Icon';
import Tooltip from './Tooltip';

interface Props
  extends StyledUtilsProps, Omit<React.HTMLProps<HTMLElement>, 'width' | 'as' | 'muted' | 'p'> {
  as?: string | ComponentType<any>;
  to?: string | Partial<Location> | ((location: Location) => Partial<Location> | string);

  /**
   * Used to indicate that the button is active in dropdowns.
   */
  active?: boolean;

  primary?: boolean;
  danger?: boolean;
  muted?: boolean;
  /**
   * Neutral buttons have a border and box shadow.
   * Previously known as "neutral"
   */
  outline?: boolean;
  /**
   * Ghost buttons have no background, borders or box shadow.
   * Previously known as "minimal" or "invisible"
   */
  ghost?: boolean;

  on?: 'background';

  icon?: IconName;

  dropdown?: boolean;
  // Override the default dropdown icon with a different expand icon
  expand_icon?: IconName;

  small?: boolean; // 20px height button, should use be used with icon
  large?: boolean; // 32px height button

  badge?: {
    label: string;
  };

  // Fill the available width
  block?: boolean;

  // States
  disabled?: boolean;
  submit?: boolean;
}

interface StyledProps extends Omit<Props, 'onClick' | 'to' | 'submit'> {
  link?: boolean;
}

export const StyledButton = styled.button<StyledProps>(
  ({ theme, link, disabled, large, small, ...props }) => {
    const is_disabled = disabled || !!props['aria-disabled'];

    const size = large ? 'large' : small ? 'small' : 'medium';

    const padding_for_size = {
      small: {
        x: 0,
        y: theme.spacing(1),
      },
      medium: {
        x: theme.spacing(1),
        y: theme.spacing(2),
      },
      large: {
        x: theme.spacing(1.5),
        y: theme.spacing(3),
      },
    };

    const styles = css`
      display: inline-flex;
      justify-content: center;
      align-items: center;
      text-align: center;
      white-space: nowrap;
      cursor: pointer;
      outline: none;
      border: none;
      text-decoration: none;
      box-sizing: border-box;
      position: relative;

      border-radius: ${theme.radius.normal};
      font-weight: ${theme.font_weights.medium};
      font-size: ${theme.pxToRem(theme.font_sizes.m.font_size)};
      line-height: ${theme.pxToRem(theme.font_sizes.m.line_height)};
      padding: ${padding_for_size[size].x} ${padding_for_size[size].y};
      color: ${theme.colors.on.neutral.primary_neutral};

      ${StyledIcon} {
        color: ${theme.colors.on.neutral.tertiary_neutral};
        fill: ${theme.colors.on.neutral.tertiary_neutral};
      }

      ${props.block &&
      css`
        width: 100%;
      `};

      &:focus-visible {
        box-shadow: ${theme.shadows.neutral.focused};
      }

      ${!props.outline &&
      !props.ghost &&
      theme.optionsToCss(
        {
          primary: css`
            color: ${theme.colors.on.hue.primary};
            background-color: ${theme.colors.surface.base.primary};
            box-shadow: ${theme.shadows.primary.base};
            ${StyledIcon} {
              fill: ${theme.colors.on.hue.primary};
              color: ${theme.colors.on.hue.primary};
            }
            &:hover {
              background-color: ${theme.colors.surface.base.hover.primary};
            }
            ${props.active &&
            css`
              background-color: ${theme.colors.surface.base.hover.primary};
            `}
          `,
          danger: css`
            color: ${theme.colors.on.hue.danger};
            background-color: ${theme.colors.surface.base.danger};
            box-shadow: ${theme.shadows.danger.base};
            ${StyledIcon} {
              fill: ${theme.colors.on.hue.danger};
              color: ${theme.colors.on.hue.danger};
            }
            &:hover {
              background-color: ${theme.colors.surface.base.hover.danger};
            }
            ${props.active &&
            css`
              background-color: ${theme.colors.surface.base.hover.danger};
            `}
          `,
        },
        props,
        'primary',
      )}

      ${(props.outline || props.ghost) &&
      theme.optionsToCss(
        {
          primary: css`
            color: ${theme.colors.on.neutral.primary};
            ${StyledIcon} {
              fill: ${theme.colors.on.neutral.primary};
              color: ${theme.colors.on.neutral.primary};
            }
          `,
          danger: css`
            color: ${theme.colors.on.neutral.danger};
            ${StyledIcon} {
              fill: ${theme.colors.on.neutral.danger};
              color: ${theme.colors.on.neutral.danger};
            }
          `,
          muted: css`
            color: ${theme.colors.on.neutral.secondary_neutral};
            ${StyledIcon} {
              fill: ${theme.colors.on.neutral.secondary_neutral};
              color: ${theme.colors.on.neutral.secondary_neutral};
            }
          `,
        },
        props,
      )}

    ${props.ghost &&
      css`
        background-color: transparent;

        &:hover {
          background-color: ${props.on === 'background'
            ? theme.colors.surface.base.hover.neutral_variant
            : theme.colors.surface.base.hover.neutral};
          box-shadow: ${theme.shadows.neutral.base};
        }

        ${props.active &&
        css`
          background-color: ${props.on === 'background'
            ? theme.colors.surface.base.hover.neutral_variant
            : theme.colors.surface.base.hover.neutral};
          box-shadow: ${theme.shadows.neutral.base};
        `}
      `}

    ${props.outline &&
      css`
        background-color: ${theme.colors.surface.base.surface};
        box-shadow: ${theme.shadows.neutral.base};
        &:hover {
          background-color: ${theme.colors.surface.base.hover.neutral};
        }

        ${props.active &&
        css`
          background-color: ${theme.colors.surface.base.hover.neutral};
        `}
        // Set border with absolute ::after to maintain alignment
        &::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          border: 0;
          border-radius: ${theme.radius.normal};
        }
      `}

    ${is_disabled &&
      css`
        cursor: not-allowed;
        color: ${theme.colors.on.neutral.disabled};
        background-color: transparent;
        box-shadow: ${props.ghost ? 'none' : theme.shadows.neutral.base};

        &:focus {
          box-shadow: ${theme.shadows.neutral.base};
        }

        ${StyledIcon} {
          fill: ${theme.colors.on.neutral.disabled};
          color: ${theme.colors.on.neutral.disabled};
          cursor: no-drop;
        }
        &:hover {
          background-color: transparent;
          box-shadow: ${props.ghost ? 'none' : theme.shadows.neutral.base};
        }

        ${props.active &&
        css`
          background-color: transparent;
          box-shadow: ${props.ghost ? 'none' : theme.shadows.neutral.base};
        `}

        ${!props.ghost &&
        css`
          background-color: ${theme.colors.surface.base.disabled};
          box-shadow: ${theme.shadows.neutral.base};

          &:hover {
            background-color: ${theme.colors.surface.base.disabled};
            box-shadow: ${theme.shadows.neutral.base};
          }

          &::after {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            border: 0;
            border-radius: ${theme.radius.normal};
          }
        `}
      `};
    `;

    return link
      ? css`
          ${(props) => getStyledUtils(props)}
          ${props.block &&
          css`
            width: 100%;
          `};
          a {
            ${styles}
          }
        `
      : css`
          ${(props) => getStyledUtils(props)}
          ${styles}
        `;
  },
);

const StyledButtonGroup = styled(Div)(
  ({ theme }) => css`
    display: inline-flex;
    align-items: center;
    border-radius: ${theme.radius.normal};
    box-shadow: ${theme.shadows.neutral.base};
    overflow: hidden;

    > div {
      height: 100%;
    }
    > div:first-child {
      flex-grow: 1;
    }
  `,
);

const StyledButtonInGroup = styled(Div)<{ first: boolean; last: boolean }>(
  ({ theme, first, last }) => css`
    ${StyledButton}:first-of-type:not(${StyledDropdown} *), ${StyledClickableArea}:first-of-type:not(${StyledDropdown} *) {
      height: 100%;
      border: none;
      width: 100%;
      box-shadow: none;
      border-right: 1px solid ${theme.colors.outline.neutral};

      &:focus-visible {
        z-index: 1;
        outline: 2px solid ${theme.colors.outline.focus.primary};
        outline-offset: 2px;
      }

      ${first &&
      !last &&
      css`
        border-radius: ${theme.radius.normal_inset} 0 0 ${theme.radius.normal_inset};
        &::after {
          border-radius: ${theme.radius.normal_inset} 0 0 ${theme.radius.normal_inset};
        }
        &:focus-visible {
          &::before {
            border-radius: ${theme.radius.normal} 0 0 ${theme.radius.normal};
          }
        }
      `}
      ${last &&
      !first &&
      css`
        border-radius: 0 ${theme.radius.normal_inset} ${theme.radius.normal_inset} 0;
        border-right: none;
        &::after {
          border-left: none;
          border-radius: 0 ${theme.radius.normal_inset} ${theme.radius.normal_inset} 0;
        }
        &:focus-visible {
          &::before {
            border-radius: 0 ${theme.radius.normal_inset} ${theme.radius.normal_inset} 0;
          }
        }
      `}
      ${!first &&
      !last &&
      css`
        border-radius: 0;
        &::after {
          border-radius: 0;
          border-left: none;
        }
        &:focus-visible {
          &::before {
            border-radius: 0;
          }
        }
      `};
    }
  `,
);

interface ButtonGroupProps extends StyledUtilsProps {
  more_options?: DropdownMenuOption[];
  small?: boolean;
}

export const ButtonGroup: React.FC<PropsWithChildren<ButtonGroupProps>> = ({
  children,
  more_options,
  ...props
}) => {
  const childs = Children.toArray(children);
  return (
    <StyledButtonGroup {...props}>
      {childs.map((child, i) => {
        return (
          <StyledButtonInGroup
            key={i}
            first={i === 0}
            last={more_options ? false : i === childs.length - 1}>
            {child}
          </StyledButtonInGroup>
        );
      })}
      {more_options && (
        <StyledButtonInGroup first={false} last={true}>
          <DropdownMenu options={more_options} outline p={0} placement="bottom-end" />
        </StyledButtonInGroup>
      )}
    </StyledButtonGroup>
  );
};

export const StyledIconButton = styled(StyledButton)(({ theme, link, small, large }) => {
  const size = large ? 'large' : small ? 'small' : 'medium';

  const padding_for_size = {
    small: theme.spacing(0.5),
    medium: theme.spacing(1.5),
    large: theme.spacing(2),
  };

  const styles = css`
    padding: ${padding_for_size[size]};
    border-radius: ${theme.radius.normal};

    a {
      display: block;
    }
  `;
  return link
    ? css`
        a {
          ${styles}
        }
      `
    : styles;
});

const Button = React.forwardRef<any, PropsWithChildren<Props>>(
  ({ as, to, submit, children, icon, dropdown, badge, expand_icon, ...props }, ref) => {
    if (to && !props.disabled) {
      if (icon && !children) {
        return (
          <StyledIconButton ref={ref} {...props} link as={'div' as any}>
            <Link to={to}>
              <Icon
                icon={icon}
                danger={props.danger && !props.disabled}
                muted={props.disabled}
                pointer
              />
            </Link>
          </StyledIconButton>
        );
      }

      if (!icon && !children && dropdown) {
        return (
          <StyledIconButton ref={ref} {...props} link as={'div' as any}>
            <Link to={to}>
              <Icon
                icon={expand_icon || 'expand_more'}
                danger={props.danger && !props.disabled}
                muted={props.disabled}
                pointer
              />
            </Link>
          </StyledIconButton>
        );
      }

      return (
        <StyledButton ref={ref} {...props} link as={'div' as any}>
          <Link to={to}>
            {icon && (
              <Icon
                icon={icon}
                left={2}
                danger={props.danger && !props.disabled}
                muted={props.disabled}
                pointer
              />
            )}
            {children}
            {dropdown && (
              <Icon
                icon={expand_icon || 'expand_more'}
                right={2}
                style={{ marginLeft: 'auto' }}
                pointer
              />
            )}
          </Link>
        </StyledButton>
      );
    }

    /**
     * There's a browser quirk where disabled elements don't fire events. This affects tooltips
     * where tooltips around disabled buttons won't disappear. Therefore, instead of setting "disabled"
     * prop, we're using "aria-disabled" and manually disabling the button.
     *
     * @see https://github.com/facebook/react/issues/18753
     * @see https://floating-ui.com/docs/react#disabled-elements
     */
    const disabled_props = props.disabled
      ? {
          disabled: false,
          'aria-disabled': true,
          tabIndex: -1,
          onClick: (event: React.MouseEvent) => event.preventDefault(),
          onDoubleClick: (event: React.MouseEvent) => event.preventDefault(),
        }
      : {};

    /**
     * `href` only navigates on an anchor — a `<button href="...">` renders and clicks
     * happily while doing nothing at all. Default to an anchor whenever a caller passes an
     * `href` without an explicit `as`, so the mistake can't reach users again.
     */
    const element_type = as || (props.href ? 'a' : 'button');
    const is_anchor = element_type === 'a';
    const type_props = is_anchor
      ? {}
      : { type: submit ? ('submit' as const) : ('button' as const) };
    const rel_props =
      is_anchor && props.target === '_blank' && !props.rel ? { rel: 'noreferrer' } : {};

    if (icon && !children) {
      return (
        <StyledIconButton
          ref={ref}
          {...props}
          {...disabled_props}
          as={element_type}
          {...type_props}
          {...rel_props}>
          <Icon icon={icon} pointer />
        </StyledIconButton>
      );
    }

    if (!icon && !children && dropdown) {
      return (
        <StyledIconButton
          ref={ref}
          {...props}
          {...disabled_props}
          as={element_type}
          {...type_props}
          {...rel_props}>
          <Icon icon={expand_icon || 'expand_more'} pointer />
        </StyledIconButton>
      );
    }

    return (
      <StyledButton
        ref={ref}
        {...props}
        {...disabled_props}
        as={element_type}
        active={props.active}
        {...type_props}
        {...rel_props}>
        {icon && <Icon icon={icon} pointer left={2} />}
        {children}
        {badge && (
          <Badge subtle m={{ l: 2, r: dropdown ? 0 : -1 }}>
            {badge.label}
          </Badge>
        )}
        {dropdown && (
          <Icon
            pointer
            icon={expand_icon || 'expand_all'}
            right={2}
            style={props.block ? { marginLeft: 'auto' } : {}}
          />
        )}
      </StyledButton>
    );
  },
);

const ButtonPermission = forwardRef<
  any,
  React.ComponentPropsWithRef<typeof Button> & { role?: TeamRole; unauthorized_tooltip?: string }
>(({ role, unauthorized_tooltip, ...buttonProps }, ref) => {
  const authorized = useAuthorized(role);

  if (authorized) return <Button ref={ref} {...buttonProps} />;

  // if already disabled then we don't need to show the tooltip
  if (buttonProps.disabled) return <Button ref={ref} {...buttonProps} />;

  return (
    <Tooltip
      fill={buttonProps.block}
      tooltip={
        unauthorized_tooltip ||
        "You don't have permission to perform this action. Contact your admin for access."
      }>
      <Button ref={ref} {...buttonProps} disabled={buttonProps.disabled || !authorized} />
    </Tooltip>
  );
});

export interface ClickableAreaProps
  extends StyledUtilsProps, Omit<React.HTMLProps<HTMLButtonElement>, 'width' | 'as'> {
  as?: string | ComponentType<any>;
  to?: string | Partial<Location> | ((location: Location) => Partial<Location> | string);

  on?: 'background';
  danger?: boolean;
  primary?: boolean;
  rounded?: boolean;
  disabled?: boolean;
  submit?: boolean;
  block?: boolean;
  highlight?: boolean;

  no_underline?: boolean;

  linkOnClick?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
}

const StyledClickableArea = styled.button<
  ClickableAreaProps & { link?: boolean; block?: boolean; no_underline?: boolean }
>(({ theme, rounded, link, block, disabled, highlight, on, no_underline, ...props }) => {
  const styles = css`
    ${(props) => getStyledUtils(props)}

    display: block;
    width: ${block ? '100%' : 'auto'};
    padding: 0;
    box-sizing: border-box;
    text-align: left;
    white-space: nowrap;
    cursor: pointer;
    outline: none;
    background-color: unset;
    border: none;
    border-radius: ${rounded ? theme.radius.normal : 'none'};
    text-decoration: none;
    color: unset;
    position: relative;

    ${
      !disabled &&
      css`
        &:hover {
          background-color: ${on === 'background'
            ? theme.colors.surface.base.hover.neutral_variant
            : theme.colors.surface.base.hover.neutral};
        }
      `
    }

    ${
      highlight &&
      css`
        background-color: ${on === 'background'
          ? theme.colors.surface.base.hover.neutral_variant
          : theme.colors.surface.base.hover.neutral};
      `
    }

      ${
        no_underline &&
        css`
          span:first-child {
            text-decoration: none !important;
          }
        `
      }

      &:focus-visible {
        outline: 2px solid ${theme.colors.outline.focus.primary};
        outline-offset: 2px;
      }
    }

    ${theme.optionsToCss(
      {
        danger: css`
          color: ${theme.colors.on.hue_container.danger};
          &:hover {
            background-color: ${theme.colors.surface.container.danger};
          }
        `,
        primary: css`
          color: ${theme.colors.on.hue_container.primary};
        `,
      },
      props,
    )}

      &:disabled {
      cursor: not-allowed;
      box-shadow: none;
    }
  `;

  return link
    ? css`
        > a {
          ${styles}
          ${disabled &&
          css`
            pointer-events: none;
          `}
        }
      `
    : styles;
});

const ClickableAreaComponent: FC<PropsWithChildren<ClickableAreaProps>> = ({
  as,
  to,
  submit,
  block = true,
  children,
  linkOnClick,
  no_underline,
  ...props
}) => {
  if (to) {
    return (
      <StyledClickableArea {...props} block={block} link as={'div' as any}>
        <Link no_underline={no_underline} onClick={linkOnClick} to={to}>
          {children}
        </Link>
      </StyledClickableArea>
    );
  }
  return (
    <StyledClickableArea
      {...props}
      block={block}
      as={as || 'button'}
      no_underline={no_underline}
      type={submit ? 'submit' : 'button'}>
      {children}
    </StyledClickableArea>
  );
};

const ClickableAreaPermission: React.FC<
  React.ComponentPropsWithRef<typeof ClickableAreaComponent> & { role?: TeamRole }
> = ({ role, ...clickableAreaProps }) => {
  const disabled = usePermissionAwareDisabled(clickableAreaProps.disabled, role);

  return <ClickableAreaComponent {...clickableAreaProps} disabled={disabled} />;
};

export const ClickableArea = withStaticProperties(ClickableAreaComponent, {
  Permission: ClickableAreaPermission,
});

export const StyledPlaceholderButton = styled(Button)(({ theme, disabled }) => {
  return css`
    background-color: transparent;
    box-shadow: none;
    color: ${theme.colors.on.neutral.tertiary_neutral};
    justify-content: center;
    width: 100%;

    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border: 1px dashed ${theme.colors.outline.neutral};
      border-radius: ${theme.radius.normal};
    }

    ${StyledIcon} {
      fill: ${theme.colors.on.neutral.tertiary_neutral};
      color: ${theme.colors.on.neutral.tertiary_neutral};
    }

    &:hover,
    a:hover {
      background-color: transparent;
      color: ${theme.colors.on.neutral.primary};
      &::after {
        border: 1px dashed ${theme.colors.on.neutral.primary};
      }

      ${StyledIcon} {
        fill: ${theme.colors.on.neutral.primary};
        color: ${theme.colors.on.neutral.primary};
      }
    }

    ${disabled &&
    css`
      &:hover {
        color: ${theme.colors.on.neutral.tertiary_neutral};
        &::after {
          border: 1px dashed ${theme.colors.outline.neutral};
        }
      }
    `}
  `;
});

interface PlaceholderButtonProps
  extends StyledUtilsProps, Omit<React.HTMLProps<HTMLElement>, 'width' | 'as' | 'muted' | 'ref'> {}

const PlaceholderButtonComponent: React.FC<PropsWithChildren<PlaceholderButtonProps>> = (props) => {
  return <StyledPlaceholderButton icon="add" {...props} />;
};

const PlaceholderButtonPermission: React.FC<
  React.ComponentPropsWithRef<typeof PlaceholderButtonComponent> & { role?: TeamRole }
> = ({ role, ...props }) => {
  const disabled = usePermissionAwareDisabled(props.disabled, role);

  return <PlaceholderButtonComponent {...props} disabled={disabled} />;
};

export const PlaceholderButton = withStaticProperties(PlaceholderButtonComponent, {
  Permission: PlaceholderButtonPermission,
});

export default withStaticProperties(Button, { Permission: ButtonPermission });
